// src/services/BackendService.js

const TOKEN_KEY = "taxi_device_token";

const config = {
  host: process.env.REACT_APP_API_HOST || 'https://x8jtju0ev6.execute-api.af-south-1.amazonaws.com',
};

/* =========================
   Device Token (persistent)
========================= */

function createToken() {
  // modern browsers
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // older mobile fallback
  const arr = new Uint8Array(16);
  window.crypto?.getRandomValues?.(arr);

  return Array.from(arr || [Math.random()])
    .map((b) => (b & 15).toString(16))
    .join("");
}


function getDeviceToken() {
  let token = localStorage.getItem(TOKEN_KEY);

  if (!token) {
    token = createToken();
    localStorage.setItem(TOKEN_KEY, token);
  }

  return token;
}

/* =========================
   Location (best effort)
========================= */

async function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        });
      },
      () => resolve(null), // permission denied or fail
      {
        enableHighAccuracy: true,
        timeout: 4000,
        maximumAge: 60000,
      }
    );
  });
}

/* =========================
   Mock ratings
========================= */

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function generateRatings() {
  const base = randomBetween(0, 5);
  //one in ten oddds to return null for rating
  return Array.from({ length: base }).map(() =>
    randomBetween(0, 5)
  );
}

/* =========================
   Service
========================= */
class BackendService {
  static async postFeedback(feedback) {
    const message = feedback?.trim();
    if (!message) return null;

    const deviceID = getDeviceToken();
    const payload = { feedback: message, deviceID };

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${config.host}/feedback`, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('x-device-token', deviceID);

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (!xhr.responseText) {
            resolve({});
            return;
          }
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve({});
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || 'Failed to submit feedback'));
          } catch {
            reject(new Error('Failed to submit feedback'));
          }
        }
      };

      xhr.onerror = function() {
        reject(new Error('Failed to submit feedback'));
      };

      xhr.send(JSON.stringify(payload));
    });
  }

  static async postRating(numberPlate, rating) {
    if ( !numberPlate || rating == null ) {
      return null;
    }
    //JSON body { "number_plate": "AAA 123", "rating": 4 }
    const location = await getLocation();
    while ( numberPlate.indexOf(" ") >= 0 ) {
      numberPlate = numberPlate.replace(" ", "");
    }
    const payload = {
      number_plate: numberPlate,
      rating: rating,
    };

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${config.host}/rating`, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('x-device-token', getDeviceToken());
      if (location) {
        xhr.setRequestHeader('x-lat', location.lat);
        xhr.setRequestHeader('x-lng', location.lng);
        xhr.setRequestHeader('x-acc', location.acc);
      }

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (error) {
            reject(new Error('Failed to parse response data'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || 'Authentication failed'));
          } catch (error) {
            reject(new Error('Authentication failed'));
          }
        }
      };

      xhr.onerror = function() {
        reject(new Error('Failed to authenticate'));
      };

      xhr.send(JSON.stringify(payload));
    });
  }

  static async getRating(numberPlate) {
    const location = await getLocation();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let url = `${config.host}/rating`;
      
      if (numberPlate) {
        url += `?plate=${encodeURIComponent(numberPlate.replace(/[^A-Z0-9]/gi, ""))}`;
      }
      
      xhr.open('GET', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('x-device-token', getDeviceToken());
      if (location) {
        xhr.setRequestHeader('x-lat', location.lat);
        xhr.setRequestHeader('x-lng', location.lng);
        xhr.setRequestHeader('x-acc', location.acc);
      }

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (error) {
            reject(new Error('Failed to parse response data'));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || 'Authentication failed'));
          } catch (error) {
            reject(new Error('Authentication failed'));
          }
        }
      };

      xhr.onerror = function() {
        reject(new Error('Failed to authenticate'));
      };

      xhr.send();
    });
  }
}

export default BackendService;
